import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import PaymentPage from "../pages/PaymentPage";
import "./Result.css";
import { FaCloud } from "react-icons/fa6";

import Stepper from "./Stepper";
import {
  computeDisplayAmount,
  computeEmissionKg,
} from "../utils/emissionUtils";

const AlertMessage = ({ type, message }) => (
  <div
    className={`alert alert-${type} mt-4 text-center fw-bold p-3 rounded shadow-sm`}
  >
    <strong>{message}</strong>
  </div>
);

function Result() {
  const location = useLocation();
  const [paymentAmount] = useState("");
  const { data, error } = location.state || {};

  if (error) return <AlertMessage type="danger" message={error} />;
  if (!data) return <AlertMessage type="warning" message="No results found." />;

  const { request = {}, googleApiResponse = {}, emissionId } = data;
  const fallbackResponse = data.response || {};
  const {
    origin = "",
    destination = "",
    passengers = 1,
    cabinClass = "economy",
    tripType = "One Way",
    email = "",
  } = request;

  // Treat calculation.response emission fields as fallback for hours-mode saved by backend
  const emissionPerPassengerFromResponse =
    googleApiResponse.emissionPerPassenger ??
    fallbackResponse.emissionPerPassenger;
  const totalEmissionFromResponse =
    googleApiResponse.totalEmission ?? fallbackResponse.totalEmission;

  const isHoursMode = !!emissionPerPassengerFromResponse;
  const isAirportMode =
    Array.isArray(googleApiResponse.flightEmissions) &&
    googleApiResponse.flightEmissions.length > 0;

  // Emission computation logic moved to ../utils/emissionUtils.js

  computeEmissionKg({ request, googleApiResponse, response: fallbackResponse });
  const displayAmount = computeDisplayAmount({
    request,
    googleApiResponse,
    response: fallbackResponse,
    paymentId: data?.paymentId,
  });

  // Step 3 should be active when viewing result, and complete after payment
  const finalStep = data?.paymentId?.status === "Paid" ? 4 : 3;

  return (
    <div className="container my-5">
      <Stepper currentStep={finalStep} />
      {/* ✅ Title */}
      <h3
        className="text-center mb-2 fw-bold"
        style={{ fontFamily: "Oswald, sans-serif", color: "#063970" }}
      >
        Flight Emissions Details
      </h3>
      <p className="text-center text-muted mb-5">
        Calculate your flight's carbon footprint and explore offset options
      </p>
      {/* ✅ Main Row */}
      <div className="row">
        {/* Left Side */}
        <div className="col-lg-8 mb-4">
          <div className="warning-box mb-3">
            ⚠️ No Direct flight found on our records. If there are stopovers,
            calculate each leg separately. Otherwise, check the estimated amount
            and pay as you prefer.
          </div>
          {isAirportMode && (
            <div className="card p-4 shadow-sm">
              <h5
                className="mb-3 fw-bold"
                style={{ color: "#063970", fontFamily: "Oswald, sans-serif" }}
              >
                Emissions Calculator Details
              </h5>
              <p className="text-muted">
                Estimated impact based on your flight specifications
              </p>

              <div className="card p-4 shadow-sm bg-light mb-3">
                <h6 className="fw-bold mb-3">Flight Details</h6>
                <div className="row">
                  <div className="col-6 mb-2">
                    <strong>Route:</strong> {origin} → {destination}
                  </div>
                  <div className="col-6 p-2">
                    <strong>Passengers:</strong> {passengers}
                  </div>
                  <div className="col-6 p-2">
                    <strong>Class:</strong> {cabinClass}
                  </div>
                  <div className="col-6 p-2">
                    <strong>Trip Type:</strong> {tripType}
                  </div>
                </div>
              </div>

              <div className="card p-4 shadow-sm bg-light">
                <p className="mb-0">
                  Estimate your impact and offset directly based on hours flown
                  – $10 per hour for round-trip Business Class or $7 per hour
                  for round-trip Economy Class
                </p>
              </div>
            </div>
          )}

          {isHoursMode && (
            <div className="card p-4 shadow">
              <h5
                className="fw-bold"
                style={{ color: "#063970", fontFamily: "Oswald, sans-serif" }}
              >
                Calculation by flying Hours
              </h5>
              <p className="text-muted">
                Estimated impact based on your flight specifications
              </p>

              <div className="card p-4 shadow-sm bg-light">
                <h6
                  className="fw-bold mb-3"
                  style={{ fontFamily: "Oswald, sans-serif",  }}
                >
                  CO₂-Emissions Details
                </h6>

                <p>
                  <strong
                    style={{
                      color: "#063970",
                      fontFamily: "Oswald, sans-serif",
                    }}
                  >
                    Per Passenger Emission:
                  </strong>{" "}
                  {((emissionPerPassengerFromResponse || 0) / 1000).toFixed(3)}
                  t CO₂e
                </p>
                {/* <p>
                  <strong>Emission per Passenger:</strong>{" "}
                  {(emissionPerPassengerFromResponse || 0).toFixed(2)} kgCO₂e
                </p> */}
                <p>
                  <strong
                    style={{
                      color: "#063970",
                      fontFamily: "Oswald, sans-serif",
                    }}
                  >
                    Total Carbon Footprint:
                  </strong>{" "}
                  {/* {(totalEmissionFromResponse || 0 / 1000).toFixed(2)} kgCO₂e */}
                  {((totalEmissionFromResponse || 0) / 1000).toFixed(3)}t CO₂e
                </p>
              </div>
              <br />

              <div className="card p-4 mb-1 shadow-sm bg-light">
                <p className="mb-0">
                  These values are estimated using standard aviation emission
                  factors. Actual emissions may vary depending on aircraft type
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Right Side */}
        <div className="col-lg-4 mb-4">
          <div className="card p-4 shadow-sm mb-3 text-center">
            <h6 className="fw-bold">Your Carbon Footprint</h6>
            <p className="mb-1">
              {isHoursMode
                ? "Flying Hours Result"
                : `${origin} → ${destination}`}
            </p>
            <div className="cloud-box">
              <FaCloud color="#00e68a" size={70} />
            </div>
            <p
              className=""
              style={{ color: "#063970", fontFamily: "Oswald, sans-serif" }}
            >
              Passenger-wise Emission:{" "}
              <strong>
                {((emissionPerPassengerFromResponse || 0) / 1000).toFixed(3)}
              </strong>
              t CO₂e
            </p>
          </div>

          <div className="card p-4 shadow-sm mb-3">
            <h6
              className="fw-bold text-center"
              style={{ fontFamily: "Oswald, sans-serif" }}
            >
              Flight Footprint
            </h6>
            <p className="text-muted text-center">
              Estimated impact based on your flight
            </p>
            <PaymentPage
              emissionId={emissionId}
              email={email}
              amount={paymentAmount ? Number(paymentAmount) : displayAmount}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Result;
